import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

interface InstallPrompt extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}
const InstallContext = createContext({ available: false, busy: false, install: async () => {} });
const dismissedKey = 'rental-news-b-install-dismissed';

export function PwaInstallProvider({ children }: { children: React.ReactNode }) {
  const [available, setAvailable] = useState(false);
  const [busy, setBusy] = useState(false);
  const promptRef = useRef<InstallPrompt | null>(null);
  const installing = useRef(false);
  const installed = useRef(false);
  const declined = useRef(false);
  useEffect(() => {
    const media = window.matchMedia('(display-mode: standalone)');
    const standalone = () => media.matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
    const dismissed = () => { try { return declined.current || sessionStorage.getItem(dismissedKey) === 'true'; } catch { return declined.current; } };
    const sync = () => setAvailable(!!promptRef.current && !standalone() && !installed.current && !dismissed());
    const before = (event: Event) => {
      const prompt = event as InstallPrompt;
      if (typeof prompt.prompt !== 'function') return;
      event.preventDefault(); promptRef.current = prompt; sync();
    };
    const completed = () => { installed.current = true; promptRef.current = null; sync(); };
    window.addEventListener('beforeinstallprompt', before);
    window.addEventListener('appinstalled', completed);
    media.addEventListener?.('change', sync);
    sync();
    return () => {
      window.removeEventListener('beforeinstallprompt', before);
      window.removeEventListener('appinstalled', completed);
      media.removeEventListener?.('change', sync);
    };
  }, []);
  const install = async () => {
    const prompt = promptRef.current;
    if (!prompt || installing.current) return;
    installing.current = true; setBusy(true);
    try {
      await prompt.prompt();
      const choice = await prompt.userChoice;
      if (choice.outcome === 'dismissed') { declined.current = true; try { sessionStorage.setItem(dismissedKey, 'true'); } catch { /* 저장 불가 환경에서는 현재 안내만 닫는다. */ } }
    } catch { /* 설치 UI를 지원하지 않거나 종료한 경우에는 자동 재시도하지 않는다. */ }
    finally { promptRef.current = null; setAvailable(false); setBusy(false); installing.current = false; }
  };
  return <InstallContext.Provider value={{ available, busy, install }}>{children}</InstallContext.Provider>;
}

export const usePwaInstall = () => useContext(InstallContext);
